import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ErrorState, Spinner } from '@/components/ui';
import { hasAutoLoginHint } from '@/lib/autoLogin';
import { useMinimumDuration } from '@/lib/hooks/useMinimumDuration';
import { useMe } from './useAuth';

/**
 * 스피너를 최소한 이만큼은 보여준다.
 *
 * 홈서버 응답이 빠르면 200ms 만에 끝나 스피너가 번쩍이고 사라진다.
 * 무언가 지나갔다는 것만 남고 무엇이었는지는 못 읽는다.
 *
 * 🔴 900ms 를 더하는 것이 아니라 하한이다.
 *   이미 그만큼 걸렸으면 추가로 기다리지 않는다. (useMinimumDuration)
 *   그리고 앱을 처음 열 때만 걸린다 — 세션 조회는 한 번 받으면 캐시된다.
 */
const SPINNER_MINIMUM_MS = 900;

/**
 * 로그인과 메뉴 권한을 확인한 뒤에만 자식을 그린다.
 *
 * 🔴 이것은 보안 장치가 아니다
 *   브라우저 개발자도구로 상태를 고치면 화면은 열린다.
 *   실제 차단은 백엔드 MenuAccessAuthorizationManager 가 한다.
 *   여기는 "권한 없는 화면을 보여주고 나서 API 가 403 을 뱉는" 경험을 막기 위한 것이다.
 *
 * 판정은 세 갈래다.
 *   확인 중   - 스피너. 여기서 바로 로그인 페이지로 보내면 새로고침마다 깜빡인다
 *   비로그인  - /login 으로. 원래 가려던 경로를 state 에 실어 로그인 후 돌아오게 한다
 *   권한 없음 - 안내 문구. 로그인 페이지로 보내면 안 된다.
 *               다시 로그인해도 같은 화면에서 또 막혀 무한히 오간다
 */
export function ProtectedRoute({
  children,
  /** 이 화면을 여는 데 필요한 메뉴 코드. 생략하면 로그인만 확인한다 */
  menuCode,
  /** 관리자만 볼 수 있는 화면인지 */
  adminOnly = false,
}: {
  children: ReactNode;
  menuCode?: string;
  adminOnly?: boolean;
}) {
  const location = useLocation();
  const { me, isLoading, isUnauthenticated, error, refetch } = useMe();

  /*
    자동 로그인 중임을 알려준다. (2026-08-17)

    "로그인 유지" 를 켠 사람은 앱을 열면 세션 쿠키로 그대로 들어간다.
    그동안 화면에 "확인 중" 만 떠 있으면 로그인이 되고 있다는 느낌이 없다.

    🔴 로그인한 적 없는 사람에게는 이 문구를 쓰지 않는다
      그 사람은 곧 로그인 화면으로 간다. "로그인 중입니다" 는 거짓말이 된다.
      켠 적이 있는지는 localStorage 표시로 안다 — 쿠키는 HttpOnly 라 못 읽는다.
      (lib/autoLogin.ts)
  */
  const 자동로그인 = hasAutoLoginHint();
  const 보여줄까 = useMinimumDuration(isLoading, SPINNER_MINIMUM_MS);

  if (isLoading || 보여줄까) {
    return <Spinner label={자동로그인 ? '로그인 중입니다' : '확인 중'} />;
  }

  if (error) {
    // 401 이 아닌 진짜 오류다. 로그인 페이지로 보내면 원인을 감추게 된다.
    return <ErrorState message={error.message} onRetry={() => void refetch()} />;
  }

  if (isUnauthenticated || !me) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (adminOnly && !me.admin) {
    return <AccessDenied />;
  }

  // 관리자는 메뉴 목록에 전 메뉴가 들어 있으므로 이 검사를 그대로 통과한다.
  if (menuCode && !me.menus.some((menu) => menu.code === menuCode)) {
    return <AccessDenied />;
  }

  return <>{children}</>;
}

function AccessDenied() {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
      <p className="text-sm font-medium text-fg">접근 권한이 없습니다.</p>
      <p className="text-xs text-fg-muted">
        이 화면이 필요하면 관리자에게 권한을 요청하세요.
      </p>
    </div>
  );
}
